import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { eventBus } from '@atc/cai-event-bus';
import { UDS_SET_DEALTYPE_REQUEST, debugWallet, UDS_SET_DEALTYPE_COMPLETED } from 'wallet-manager';
import { ndsNavigationClick } from 'reaxl-analytics-handlers';
import { sendClick } from 'reaxl-analytics';
import _capitalize from 'lodash/capitalize';
import buildPayments from '../helpers/buildPayments.js';

/**
 * Hook that handles external deal type changes via event bus. Updates active tab and wallet payments
 * info when `UDS_SET_DEALTYPE_REQUEST` is received, then emits `UDS_SET_DEALTYPE_COMPLETED`.
 *
 * @param {string[]} supportedDealTypes - Array of supported deal types (e.g. 'finance', 'lease', 'cash')
 * @param {Function} setActiveTab - Function to update the active deal type tab
 * @param {Function} updatePaymentsInfo - Function to update the payments info in the wallet
 * @param paymentsDuck
 * @param analyticsData
 * @param handleNDSClick
 * @param selectedCriteria
 * @param obfuscatedCredit
 * @returns {void}
 */
const useExternalDealTypeChange = function (supportedDealTypes, setActiveTab, updatePaymentsInfo, paymentsDuck) {
  let analyticsData = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  let handleNDSClick = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : () => {};
  let selectedCriteria = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
  let obfuscatedCredit = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : null;
  const {
    listingId,
    price,
    offerPrice
  } = analyticsData;

  // Get the latest paymentsInfo from the store
  const paymentsInfo = useSelector(state => paymentsDuck?.selectors?.getPaymentsInfo(state)) || {};
  const {
    interactions: {
      paymentEmphasis
    } = {}
  } = paymentsInfo;
  const updatedPaymentInfo = {
    ...paymentsInfo,
    /* When wallet is not enabled and Tabs are pencilled first time override wallet with UDS selections */
    ...(!paymentEmphasis && {
      ...buildPayments(selectedCriteria, obfuscatedCredit),
      interactions: {
        ...paymentsInfo.interactions,
        paymentEmphasis: true
      }
    })
  };
  useEffect(() => {
    const handleDealTypeChange = _ref => {
      let {
        dealType: requestedDealType,
        placement = 'nds',
        event
      } = _ref;
      if (requestedDealType && supportedDealTypes.includes(requestedDealType)) {
        setActiveTab(requestedDealType);
        // update wallet
        updatePaymentsInfo({
          ...updatedPaymentInfo,
          dealType: requestedDealType
        });
        const userModified = 'DEAL_TAB_CHANGED';
        const eventLabel = `${requestedDealType}-tab${placement === 'deal-type-tabs' ? '-deal-incentive' : ''}`;
        sendClick(ndsNavigationClick, event, {
          cmp: eventLabel,
          eventResult: eventLabel,
          listingId,
          walletData: {
            paymentsInfo: {
              ...updatedPaymentInfo,
              dealType: requestedDealType
            },
            userModified,
            price,
            offerPrice
          },
          key: requestedDealType
        });
        handleNDSClick(event, {
          userModified,
          elementText: _capitalize(requestedDealType),
          targetTab: requestedDealType
        });
        debugWallet('UDS', 'uds:setDealType:completed', requestedDealType);
        eventBus.emit(UDS_SET_DEALTYPE_COMPLETED);
      }
    };
    eventBus.on(UDS_SET_DEALTYPE_REQUEST, handleDealTypeChange);
    return () => eventBus.off(UDS_SET_DEALTYPE_REQUEST, handleDealTypeChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
export { useExternalDealTypeChange as default };
